import { apiGet } from './api-client';

interface TfNSWDepartureStop {
  id: string;
  name: string;
  type: string;
  coord?: [number, number];
}

interface TfNSWDepartureEvent {
  departureTimePlanned: string;
  departureTimeEstimated?: string;
  transportation: {
    number: string;
    disassembledName?: string;
    origin?: { name: string };
    destination?: { name: string };
    description?: string;
    product: { name: string; class?: number; iconId?: number };
    properties?: { REAL_TIME?: string; TRIP_CODE?: string; isTTB?: boolean };
  };
  platform?: { name: string };
  tripId?: string;
  isReachable?: boolean;
  location?: TfNSWDepartureStop;
  previousLocations?: TfNSWRouteLocation[];
  onwardLocations?: TfNSWRouteLocation[];
  properties?: { RealtimeTripId?: string; WheelchairAccess?: string };
}

interface TfNSWRouteLocation extends TfNSWDepartureStop {
  arrivalTimePlanned?: string;
  departureTimePlanned?: string;
  arrivalTimeEstimated?: string;
  departureTimeEstimated?: string;
}

interface TfNSWDepartureResponse {
  stop?: TfNSWDepartureStop[];
  stopEvents?: TfNSWDepartureEvent[];
}

export interface Departure {
  tripId: string;
  routeNumber: string;
  routeShortName?: string;
  routeName: string;
  destination: string;
  origin?: string;
  mode: string;
  modeClass: number;
  scheduledTime: string;
  predictedTime?: string;
  delayMinutes?: number;
  isRealtime: boolean;
  platform?: string;
  isCancelled?: boolean;
  stopCoords?: [number, number];
  serviceStops?: ServiceStop[];
  /** TfNSW realtime trip id (matches GTFS-RT trip updates), when available. */
  realtimeTripId?: string;
}

export interface ServiceStop {
  id: string;
  name: string;
  coords?: [number, number];
  arrival?: string;
  departure?: string;
}

function serviceStop(location: TfNSWRouteLocation): ServiceStop {
  return {
    id: location.id,
    name: location.name,
    coords: location.coord ? [location.coord[1], location.coord[0]] : undefined,
    arrival: location.arrivalTimeEstimated || location.arrivalTimePlanned,
    departure: location.departureTimeEstimated || location.departureTimePlanned,
  };
}

const MODE_NAMES: Record<number, string> = {
  1: 'Train', 2: 'Train', 3: 'Train', 4: 'Light Rail',
  5: 'Bus', 6: 'Bus', 7: 'Bus', 8: 'Train', 9: 'Ferry',
  10: 'Bus', 11: 'Bus', 12: 'Bus',
};

export function departureMode(product: { name?: string; class?: number }, description = '', routeNumber = ''): string {
  const identity = `${product.name || ''} ${description} ${routeNumber}`.toLocaleLowerCase();
  // TfNSW reports Sydney Metro with product class 2, which is also used by
  // heavy rail. Product identity must therefore win over class alone.
  if (identity.includes('metro') || /^m\d\b/i.test(routeNumber.trim())) return 'Metro';
  if (identity.includes('light rail') || identity.includes('lightrail') || /^l\d\b/i.test(routeNumber.trim())) return 'Light Rail';
  if (identity.includes('ferr') || /^f\d\b/i.test(routeNumber.trim())) return 'Ferry';
  // School buses / coaches are reported with class 11 but are buses, not light rail.
  if (identity.includes('bus') || identity.includes('coach') || identity.includes('school')) return 'Bus';
  if (identity.includes('train') || identity.includes('rail')) return 'Train';
  return MODE_NAMES[product.class ?? 5] || 'Transit';
}

export async function getDepartures(stopId: string, limit = 20, signal?: AbortSignal, timeParams?: { itdDate?: string; itdTime?: string }): Promise<Departure[]> {
  const data = await apiGet<TfNSWDepartureResponse>('/tp/departure_mon', {
    params: {
      type_dm: 'stop',
      name_dm: stopId,
      mode: 'direct',
      limit: String(limit),
      outputFormat: 'rapidJSON',
      coordOutputFormat: 'EPSG:4326',
      TfNSWDM: 'true',
      departureMonitorMacro: 'true',
      nameKey_dm: '$USEPOINT$',
      ...(timeParams?.itdDate ? { itdDate: timeParams.itdDate } : {}),
      ...(timeParams?.itdTime ? { itdTime: timeParams.itdTime } : {}),
    },
    signal,
  });

  if (!data?.stopEvents) return [];

  const rawCoord = data.stop?.[0]?.coord;
  const stopCoords: [number, number] | undefined = rawCoord ? [rawCoord[1], rawCoord[0]] : undefined;

  return data.stopEvents.map((ev) => {
    const modeClass = ev.transportation.product.class ?? 5;
    const mode = departureMode(ev.transportation.product, ev.transportation.description, ev.transportation.number);
    const schedTime = ev.departureTimePlanned;
    const predTime = ev.departureTimeEstimated;
    // TfNSW marks almost every timetable service with `isTTB: true` — that is
    // NOT a realtime signal. A live estimate exists only when
    // `departureTimeEstimated` is present (or REAL_TIME='1'). Use those to
    // decide realtime so the UI shows LIVE/estimate info only where it exists.
    const isLive = ev.transportation.properties?.REAL_TIME === '1'
      || !!predTime;

    let delayMinutes: number | undefined;
    if (predTime && schedTime) {
      const s = new Date(schedTime).getTime();
      const p = new Date(predTime).getTime();
      delayMinutes = Math.round((p - s) / 60000);
    }

    const currentLocation: TfNSWRouteLocation = ev.location || data.stop?.[0] || { id: stopId, name: data.stop?.[0]?.name || stopId, type: 'stop', coord: rawCoord };
    const serviceStops = [...(ev.previousLocations || []), currentLocation, ...(ev.onwardLocations || [])]
      .map(serviceStop)
      .filter((s, i, all) => i === 0 || s.id !== all[i - 1].id);

    return {
      tripId: ev.tripId || ev.transportation.number,
      routeNumber: ev.transportation.number,
      routeShortName: ev.transportation.disassembledName || ev.transportation.number,
      routeName: ev.transportation.description || ev.transportation.product.name,
      destination: ev.transportation.destination?.name || '',
      origin: ev.transportation.origin?.name,
      mode,
      modeClass,
      scheduledTime: schedTime,
      predictedTime: predTime,
      delayMinutes,
      isRealtime: isLive,
      platform: ev.platform?.name,
      stopCoords,
      serviceStops,
      realtimeTripId: ev.properties?.RealtimeTripId,
    };
  });
}

/**
 * Enrich departures with GTFS-RT delay data, matched by TfNSW realtime trip id.
 *
 * TfNSW only supplies `departureTimeEstimated` for imminent departures, and
 * never for past services. The GTFS-RT trip-updates feeds carry live delays
 * for every active trip, keyed by the same realtime trip id the departure
 * monitor reports. When a matching update is found, we compute a better
 * predicted time and delay badge.
 *
 * @param deps     Departures to enrich (mutated in place, returns same array)
 * @param delays   Map<realtimeTripId, Map<stopId, delaySeconds>> from fetchTripStopDelays
 * @param stopId   Stop whose delay should be used (falls back to max trip delay)
 */
export function enrichDeparturesWithGtfsRt(
  deps: Departure[],
  delays: Map<string, Map<string, number>>,
  stopId: string,
): Departure[] {
  for (const d of deps) {
    if (d.isRealtime || !d.realtimeTripId) continue; // keep existing estimates
    const perStop = delays.get(d.realtimeTripId);
    if (!perStop) continue;
    const delaySec = perStop.get(stopId) ?? [...perStop.values()][0];
    if (delaySec == null) continue;
    const delayMin = Math.round(delaySec / 60);
    const schedMs = new Date(d.scheduledTime).getTime();
    d.delayMinutes = delayMin;
    d.isRealtime = true;
    d.predictedTime = new Date(schedMs + delaySec * 1000).toISOString();
  }
  return deps;
}

/**
 * Fetch all departures for a stop from a given local date/time, paging through
 * the TfNSW API. The API caps each response at ~40 events regardless of the
 * requested limit, so we page forward using the last event's time until we
 * either reach `untilTime` or get a short (non-full) page.
 */
export async function getDeparturesSince(
  stopId: string,
  itdDate: string,
  itdTime: string,
  untilTime: string,
  signal?: AbortSignal,
): Promise<Departure[]> {
  const all = new Map<string, Departure>();
  let cursor = itdTime;
  // Track the Sydney-local date of the cursor separately. When the cursor
  // wraps past midnight the API (with the same itdDate) would otherwise return
  // the same early-morning events again, creating an infinite duplicate loop
  // that never reaches `untilTime`. Advancing itdDate fixes the rollover.
  let pageDate = itdDate;
  // TfNSW caps each departure_mon response at 40 events regardless of the
  // requested limit. We must page with PAGE=40 so `page.length < PAGE` only
  // triggers on a genuinely short (final) page.
  const PAGE = 40;
  // Comparable "YYYYMMDDHHMM" target built from the caller's date + untilTime.
  const target = `${itdDate}${untilTime}`;
  // Sydney-local "YYYYMMDDHHMM" stamp for an event, used to decide whether it
  // falls at/before the target. TfNSW timestamps early-morning services of the
  // current service day as the previous UTC day, so comparing in Sydney local
  // time (not UTC) is what keeps "past services" from leaking future runs.
  const sydneyStamp = (iso: string) => new Date(iso).toLocaleString('en-CA', {
    timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).replace(/[-: ,]/g, '');
  for (let i = 0; i < 12; i++) {
    const page = await getDepartures(stopId, PAGE, signal, { itdDate: pageDate, itdTime: cursor });
    if (!page.length) break;
    // Only keep events at or before the target (Sydney local time). The API
    // returns the whole service day from its first run, so without this filter
    // early-morning future services would be mislabelled as "past".
    for (const d of page) {
      if (sydneyStamp(d.scheduledTime) <= target) all.set(`${d.tripId}|${d.scheduledTime}`, d);
    }
    // Stop when we've reached the target time or the page wasn't full.
    if (page.length < PAGE) break;
    const last = page[page.length - 1].scheduledTime;
    // Sydney-local date+time of the last event as "YYYYMMDDHHMM".
    const lastStamp = sydneyStamp(last);
    if (lastStamp >= target) break;
    // Advance the cursor just past the last event, carrying its local date so
    // the next request uses the correct itdDate across a midnight boundary.
    const lastDate = new Date(last);
    lastDate.setMinutes(lastDate.getMinutes() + 1);
    const nextStamp = sydneyStamp(lastDate.toISOString());
    pageDate = nextStamp.slice(0, 8);
    cursor = nextStamp.slice(8);
    // Safety: if the cursor didn't advance, stop to avoid an infinite loop.
    if (nextStamp <= lastStamp) break;
  }
  return [...all.values()].sort((a, b) => new Date(a.scheduledTime).getTime() - new Date(b.scheduledTime).getTime());
}
